/**
 * Renders one logical turn (user prompt -> thinking transcript -> final
 * response) as a single visual card.
 *
 * Visual hierarchy:
 *   1. User bubble (right-aligned, blue) -- optional; only when the user
 *      actually sent a prompt for this turn.
 *   2. ThinkingTerminal -- worker transparency stream (plain one-liners).
 *   3. BuilderStreamTerminal -- Universal Builder PTY lines (terminal chrome)
 *      with progress phases during cold start.
 *   4. "Awaiting approval..." indicator (interaction moved to Composer alert band).
 *   5. Agent final response (AgentResponsePanel); system rows stack underneath.
 *
 * The component does not own state -- turn data comes from the reducer's
 * ``TurnGroup`` shape.
 */
import React from 'react';
import ThinkingTerminal from './ThinkingTerminal.jsx';
import BuilderStreamTerminal from './BuilderStreamTerminal.jsx';
import BuilderLogCard from './BuilderLogCard.jsx';
import AgentResponsePanel from './AgentResponsePanel.jsx';

function UserBubble({ entry }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-lg bg-blue-600 px-3 py-2 text-sm text-white shadow-sm">
        <div className="text-[11px] font-medium uppercase tracking-wide text-blue-100/90">
          {entry.actorName || 'You'}
        </div>
        <div className="mt-1 whitespace-pre-wrap">{entry.text}</div>
      </div>
    </div>
  );
}

function AgentBubble({ entry, thinking, turnStatus, startedAt, endedAt, activeAgentId, networkId, poolId, taskId }) {
  // The final synthesis (published as agentName "Summary") is THE deliverable — render it
  // unbounded (full height, no small scroll window) so the whole answer spreads out.
  const isSummary = String(entry.agentName || '').trim().toLowerCase() === 'summary';
  return (
    <AgentResponsePanel
      agentName={entry.agentName}
      text={entry.text}
      contentKey={entry.id}
      thinking={thinking}
      turnStatus={turnStatus}
      startedAt={startedAt}
      endedAt={endedAt}
      activeAgentId={activeAgentId}
      usage={entry.usage}
      networkId={networkId}
      poolId={poolId}
      taskId={taskId}
      unbounded={isSummary}
    />
  );
}

function SystemBubble({ entry }) {
  return (
    <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <div className="whitespace-pre-wrap">{entry.text}</div>
    </div>
  );
}

/**
 * @param {{
 *   turn: import('@/utils/agentRoomReducer.js').TurnGroup,
 *   networkId?: string, poolId?: string,
 *   hideAgentStack?: boolean,   // when true, render the user bubble + builder surfaces only, and
 *     // SKIP agent final panels + standalone thinking — the current run's agent work is shown by
 *     // the AgentHierarchyPanel tree instead (Step 5 swap). Builder streaming/interaction stays.
 * }} props
 */
export default function TurnGroup({ turn, networkId, poolId, handledBuilderWaitSet, resolvedWaitEventIds, hideAgentStack = false }) {
  const {
    userEntry, agentEntries, thinking, builderTerminalLines,
    builderPhases, builderInputWait, builderCompletion, builderCompletions, status,
    builderStartedAt,
  } = turn;
  const hasThinking = Array.isArray(thinking) && thinking.length > 0;
  const hasBuilder = Array.isArray(builderTerminalLines) && builderTerminalLines.length > 0;
  const hasPhases = Array.isArray(builderPhases) && builderPhases.length > 0;
  // Per-agent completions win; fall back to the legacy single completion for
  // old executions whose envelopes carried no agentId.
  const completionList = (Array.isArray(builderCompletions) && builderCompletions.length > 0)
    ? builderCompletions
    : (builderCompletion ? [builderCompletion] : []);
  const hasCompletion = completionList.some(
    (c) => c && (c.logKey || c.eventsKey || c.exitCode != null),
  );
  const isLive = status === 'live';
  const showThinking = hasThinking || (isLive && !hasBuilder && !hasPhases);
  const showBuilder = hasBuilder || (isLive && hasPhases);
  const showBuilderSummary = !showBuilder && (hasCompletion || (!isLive && hasPhases));
  const builderHasContent = showBuilder || showBuilderSummary;

  // Chronological merge of agent panels and the Builder surface.
  // Without this, the Builder card was hardcoded to render *before*
  // any agent final response — which made multi-agent runs render in
  // the wrong order: e.g. Lead → Builder → Test on the wire became
  // Builder → Lead → Test on screen, because Lead's panel always
  // came after Builder UI in JSX. Now: each entry carries a ``ts``
  // (final-response timestamps for agents, ``builderStartedAt`` for
  // the Builder surface), and we render in ascending ts order.
  // Builder runs without any agent finals are unchanged — Builder
  // sits in its natural position.
  // ``ts`` on agent entries and ``builderStartedAt`` on the turn may
  // be either a number (ms epoch) or an ISO-8601 string depending on
  // the envelope source. Normalize to a comparable number and fall
  // back to ``0`` when missing so legacy envelopes render in their
  // original array order.
  const tsToMs = (v) => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v) {
      const n = Date.parse(v);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };
  const orderedAgentSlots = (() => {
    /** @type {Array<{kind:'agent'|'builder', ts:number, key:string, entry?:any}>} */
    const slots = [];
    // When the hierarchy tree owns the agent panels, skip BOTH agent finals AND the builder surface
    // here — the tree's shared reader now renders the builder too (BuilderStreamTerminal live /
    // BuilderLogCard done, off the roster row's builderLines/builderCompletion). Rendering it inline
    // as well double-showed the builder: a PTY "window above" the tree PLUS the embedded builder
    // block when its row was clicked. So the tree owns the whole agent surface (workers + builders);
    // TurnGroup keeps only the user bubble + awaiting band. Non-tree runs are unchanged.
    if (!hideAgentStack) {
      for (const e of agentEntries) {
        slots.push({ kind: 'agent', ts: tsToMs(e.ts), key: e.id, entry: e });
      }
      if (builderHasContent) {
        slots.push({ kind: 'builder', ts: tsToMs(builderStartedAt), key: '__builder' });
      }
    }
    slots.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return slots;
  })();

  return (
    <div className="space-y-2">
      {userEntry ? <UserBubble entry={userEntry} /> : null}
      {builderInputWait && isLive
        && !handledBuilderWaitSet?.has?.(builderInputWait.eventId)
        && !resolvedWaitEventIds?.has?.(builderInputWait.eventId) ? (
        <div className="mx-3 mb-2 rounded-md border border-amber-200/60 bg-amber-50/50 px-3 py-2 text-[11px] text-amber-800">
          Awaiting approval...
        </div>
      ) : null}
      {orderedAgentSlots.map((slot) => {
        if (slot.kind === 'builder') {
          if (showBuilder) {
            return (
              <BuilderStreamTerminal
                key={slot.key}
                lines={builderTerminalLines}
                status={status}
                builderPhases={builderPhases}
                agentName={turn.activeAgentId}
                networkId={networkId}
                runId={turn.builderRunId || turn.correlationId}
              />
            );
          }
          // One card per builder completion. In a multi-agent turn each agent
          // gets its own card + its own transcript (distinct logKey/eventsKey);
          // legacy single-completion turns render exactly one card as before.
          return completionList.map((c, i) => (
            <BuilderLogCard
              key={`${slot.key}-${(c && c.agentId) || i}`}
              completion={c}
              agentName={c && c.agentId}
              networkId={networkId}
              runId={turn.builderRunId || turn.correlationId}
            />
          ));
        }
        const e = slot.entry;
        return e.kind === 'system'
          ? <SystemBubble key={slot.key} entry={e} />
          : <AgentBubble key={slot.key} entry={e} thinking={showThinking ? thinking : null} turnStatus={status} startedAt={turn.startedAt} endedAt={turn.endedAt} activeAgentId={turn.activeAgentId} networkId={networkId} poolId={poolId} taskId={turn.executionId} />;
      })}
      {/* Show thinking standalone only when there's no agent response yet. Suppressed when the
          hierarchy tree owns the agent surface (the tree's shared reader shows the live work).
          ALSO suppressed when there are NO thinking steps yet: an EMPTY live ThinkingTerminal is
          just the old "waiting for agent" window — a bordered shell with a running stopwatch and
          nothing in it — that flashes in the pre-seed gap before the agent tree appears. We stream
          nothing into it, so it adds no information; the composer's "Processing…" beat covers that
          gap until the seed stack lands. Render the window only once it has real thinking lines. */}
      {!hideAgentStack && showThinking && hasThinking && agentEntries.length === 0 && !builderHasContent
        ? <ThinkingTerminal turn={turn} /> : null}
    </div>
  );
}
